import { CheckCircle } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Fade,
  FormHelperText,
  Paper,
  Rating,
  Typography,
  useTheme
} from '@mui/material';
import React, { useState } from 'react';
import { TrackedTextField } from 'src/theme/StyledFields';

export interface FinalReflectionData {
  satisfaction_rating?: number;
  reflection_text: string;
}

interface FinalReflectionFormProps {
  onSubmit: (data: FinalReflectionData) => Promise<void>;
  onSkip: () => void;
  loading?: boolean;
  error?: string | null;
}

const SATISFACTION_LABELS: { [index: number]: string } = {
  1: 'Very Dissatisfied',
  2: 'Dissatisfied',
  3: 'Neutral',
  4: 'Satisfied',
  5: 'Very Satisfied',
};

const FinalReflectionForm: React.FC<FinalReflectionFormProps> = ({
  onSubmit,
  onSkip,
  loading = false,
  error = null,
}) => {
  const theme = useTheme();
  const [satisfactionRating, setSatisfactionRating] = useState<number | undefined>(undefined);
  const [reflectionText, setReflectionText] = useState('');
  const [hover, setHover] = useState(-1);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit({
      satisfaction_rating: satisfactionRating,
      reflection_text: reflectionText,
    });
  };

  const activeRating = hover !== -1 ? hover : (satisfactionRating || 0);

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Fade in={true} timeout={600}>
        <Paper
          elevation={3}
          data-testid="final-reflection-form"
          sx={{ p: { xs: 3, sm: 6 }, borderRadius: 3 }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <CheckCircle sx={{ fontSize: 64, color: theme.palette.success.main, mb: 2 }} />
            <Typography variant="h4" gutterBottom fontWeight="bold" color="success.main">
              Assessment Complete!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Before you go, we'd love your feedback. These two questions are optional.
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit} data-attr="final-reflection-form_submit">
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Overall, how satisfied were you with your experience using the program?
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Rating
                  value={satisfactionRating || 0}
                  onChange={(_, v) => setSatisfactionRating(v || undefined)}
                  onChangeActive={(_, v) => setHover(v)}
                  max={5}
                  size="large"
                  data-attr="final-reflection-form_field-satisfaction-rating"
                />
                <Typography variant="body2" color="text.secondary">
                  {activeRating > 0 ? `${activeRating} — ${SATISFACTION_LABELS[activeRating]}` : 'Optional'}
                </Typography>
              </Box>
              <FormHelperText>1 star = Very Dissatisfied, 5 stars = Very Satisfied</FormHelperText>
            </Box>

            <TrackedTextField
              fullWidth
              multiline
              rows={5}
              label="Your reflection"
              placeholder="Share any growth, insights, skills, or feedback about your participation."
              helperText="What was the most valuable thing you learned or gained from this experience? (optional)"
              value={reflectionText}
              onChange={(e) => setReflectionText(e.target.value)}
              sx={{ mb: 4 }}
              data-attr="final-reflection-form_field-reflection-text"
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="text"
                onClick={onSkip}
                disabled={loading}
                sx={{ textTransform: 'none' }}
                data-attr="final-reflection-form_skip"
              >
                Skip
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ textTransform: 'none', px: 4 }}
                data-attr="final-reflection-form_submit-button"
              >
                {loading ? <CircularProgress size={24} /> : 'Submit Feedback'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </Container>
  );
};

export default FinalReflectionForm;
